/**
 * A tiny in-memory client-error log (spec 08 §7 resilience).
 *
 * Render crashes caught by {@link ErrorBoundary} and uncaught async errors are
 * recorded here and echoed to `console.error`, so a developer can inspect what
 * blanked (or nearly blanked) a view — both live in the console and via the
 * error fallback's "technical details". The buffer is intentionally small and
 * process-local; it is a diagnosis aid, not analytics, and never leaves the tab
 * unless a reporter is wired up.
 */

/** One recorded client error. */
export interface ClientErrorEntry {
  /** ISO timestamp the error was recorded. */
  at: string;
  /** Where it came from — a render crash or an uncaught async error. */
  source: 'render' | 'window' | 'promise';
  /** The error message. */
  message: string;
  /** The error stack, when available. */
  stack?: string | undefined;
  /** The React component stack for render crashes. */
  componentStack?: string | undefined;
  /** The route path at the time, when known. */
  path?: string | undefined;
}

/** Optional sink that forwards recorded errors (e.g. a future backend reporter). */
type ErrorReporter = (entry: ClientErrorEntry) => void;

const MAX_ENTRIES = 50;
const buffer: ClientErrorEntry[] = [];
let reporter: ErrorReporter | null = null;

/** Record a client error: keep it in the ring buffer, log it, and report it. */
export function recordClientError(entry: Omit<ClientErrorEntry, 'at'>): ClientErrorEntry {
  const full: ClientErrorEntry = { ...entry, at: new Date().toISOString() };
  buffer.push(full);
  if (buffer.length > MAX_ENTRIES) {
    buffer.shift();
  }
  // Always echo to the console so it is visible in devtools + captured by any
  // console-forwarding the operator has set up ("write something to the logs").
  // eslint-disable-next-line no-console
  console.error(`[DeployHub] ${full.source} error:`, full.message, full.stack ?? '', full.componentStack ?? '');
  try {
    reporter?.(full);
  } catch {
    // A failing reporter must never itself crash error handling.
  }
  return full;
}

/** The recorded errors, newest last. Used by the error fallback + debugging. */
export function clientErrors(): ReadonlyArray<ClientErrorEntry> {
  return [...buffer];
}

/** Wire an external reporter (e.g. POST to a backend log endpoint). */
export function setClientErrorReporter(fn: ErrorReporter | null): void {
  reporter = fn;
}
